import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User as UserIcon, Save, IdCard } from "lucide-react";
import { toast } from "sonner";
import CountryCombobox from "@/components/profile/CountryCombobox";

/**
 * Compact self-edit profile card. Two sections:
 *   1. Personal info — name, phone, address, country (powers admin broadcast targeting).
 *   2. Driver identity — passport/ID number, driver licence number + expiry. These flow
 *      into every new booking automatically (BookingWidget prefills them) so customers
 *      can manage them once here instead of re-typing at every checkout.
 *
 * The underlying endpoint (`PATCH /users/me/profile`) works for every role, but only
 * the customer dashboard surfaces this UI today.
 */
export default function ProfileCard() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    whatsapp: user?.whatsapp || "",
    address: user?.address || "",
    country: user?.country || "",
    id_number: user?.id_number || "",
    license_number: user?.license_number || "",
    license_expiry: user?.license_expiry || "",
    date_of_birth: user?.date_of_birth || "",
    // Iter 67ef — explicit GDPR opt-in flag. Treat missing/null as
    // opt-in (the historical default for accounts created before the
    // field existed) so existing renters don't see a confusingly-empty
    // toggle on first visit. The backend interprets `null` the same way.
    marketing_consent: user?.marketing_consent !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/profile", {
        name: form.name,
        phone: form.phone,
        address: form.address,
        country: form.country,  // "" → backend $unsets
        id_number: form.id_number,
        license_number: form.license_number,
        license_expiry: form.license_expiry,
        date_of_birth: form.date_of_birth,
        marketing_consent: !!form.marketing_consent,
      });
      // Iter 67z — WhatsApp lives on its own endpoint because it requires
      // E.164 normalisation server-side. Skip the call when the user didn't
      // change the field to avoid an extra HTTP round-trip.
      const initialWa = user?.whatsapp || "";
      if ((form.whatsapp || "") !== initialWa) {
        await api.post("/profile/whatsapp", { whatsapp: form.whatsapp || "" });
      }
      toast.success(t("toasts.profileSaved"));
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-3xl border border-border bg-white p-6 mb-8" data-testid="profile-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-2xl bg-neutral-100 flex items-center justify-center"><UserIcon className="h-4 w-4 text-neutral-600" /></div>
        <div>
          <h2 className="font-display text-lg font-medium">{t("dashboard.yourProfile")}</h2>
          <p className="text-xs text-neutral-500">{t("dashboard.profileHelp")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
        <div><Label>Full name</Label><Input className="mt-1 rounded-xl" value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="profile-name" /></div>
        <div><Label>Phone</Label><Input className="mt-1 rounded-xl" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="profile-phone" /></div>
        <div className="md:col-span-2">
          <Label className="flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-emerald-600" aria-hidden><path d="M20.52 3.48A11.93 11.93 0 0012.06 0C5.45 0 .06 5.39.06 12c0 2.11.55 4.16 1.6 5.97L0 24l6.18-1.62A11.94 11.94 0 0012.06 24C18.67 24 24 18.63 24 12c0-3.18-1.23-6.18-3.48-8.52zM12.06 22a9.94 9.94 0 01-5.06-1.39l-.36-.21-3.66.96.98-3.57-.24-.37A9.96 9.96 0 012.06 12c0-5.51 4.49-10 10-10s10 4.49 10 10-4.49 10-10 10z"/></svg>
            WhatsApp number
            <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">For notifications</span>
          </Label>
          <Input
            className="mt-1 rounded-xl"
            value={form.whatsapp}
            onChange={(e) => set("whatsapp")(e.target.value)}
            placeholder="+33612345678 (must include country code)"
            data-testid="profile-whatsapp"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            Used for booking-confirmation pings and direct chat with your host. Include the country code (e.g. +33, +44, +1).
          </p>
        </div>
        <div>
          <Label>Email</Label>
          <Input
            className="mt-1 rounded-xl bg-neutral-50 text-neutral-700"
            value={user?.email || ""}
            readOnly
            disabled
            data-testid="profile-email"
            title="Your sign-in email. Contact support to change it."
          />
          <p className="text-[10px] text-neutral-500 mt-1">Sign-in email — contact support to change.</p>
        </div>
        <div>
          <Label>Country</Label>
          <div className="mt-1">
            <CountryCombobox
              value={form.country || ""}
              onChange={(code) => set("country")(code)}
              placeholder="Search and pick your country…"
              testId="profile-country"
            />
          </div>
        </div>
        <div className="md:col-span-2"><Label>Address</Label><Input className="mt-1 rounded-xl" value={form.address} onChange={(e) => set("address")(e.target.value)} data-testid="profile-address" /></div>
      </div>

      {/* Driver identity — pre-fills on every booking so customers don't have to retype. */}
      <div className="mt-6 pt-5 border-t border-border max-w-2xl" data-testid="profile-identity-section">
        <div className="flex items-center gap-2 mb-1">
          <IdCard className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold">{t("dashboard.driverIdentity")}</h3>
        </div>
        <p className="text-xs text-neutral-500 mb-3">{t("dashboard.driverIdentityHelp")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Passport / ID number</Label>
            <Input
              className="mt-1 rounded-xl"
              value={form.id_number}
              onChange={(e) => set("id_number")(e.target.value)}
              placeholder="e.g. AB1234567"
              data-testid="profile-id-number"
            />
          </div>
          <div>
            <Label>Driver licence number</Label>
            <Input
              className="mt-1 rounded-xl"
              value={form.license_number}
              onChange={(e) => set("license_number")(e.target.value)}
              placeholder="e.g. DL1234567"
              data-testid="profile-license-number"
            />
          </div>
          <div>
            <Label>Licence expiry</Label>
            <Input
              type="date"
              className="mt-1 rounded-xl"
              value={form.license_expiry || ""}
              onChange={(e) => set("license_expiry")(e.target.value)}
              data-testid="profile-license-expiry"
            />
          </div>
          <div>
            <Label>Date of birth</Label>
            <Input
              type="date"
              className="mt-1 rounded-xl"
              value={form.date_of_birth || ""}
              onChange={(e) => set("date_of_birth")(e.target.value)}
              data-testid="profile-date-of-birth"
            />
            <p className="text-[10px] text-neutral-500 mt-1">Used to verify age limits set by some hosts.</p>
          </div>
        </div>
      </div>

      {/* Iter 67ef — GDPR marketing-consent toggle. Default = opt-in for
          historical users; new accounts inherit this same default unless
          the signup form explicitly captures consent (out of scope this
          iter). The unsubscribe link in every broadcast email gives users
          a second, asynchronous way to flip this. */}
      <div
        className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/40 p-4 flex items-start gap-3"
        data-testid="profile-marketing-card"
      >
        <input
          type="checkbox"
          id="marketing-consent"
          checked={!!form.marketing_consent}
          onChange={(e) => set("marketing_consent")(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
          data-testid="profile-marketing-consent"
        />
        <label htmlFor="marketing-consent" className="flex-1 cursor-pointer text-sm leading-relaxed">
          <div className="font-semibold text-neutral-900">Receive marketing emails</div>
          <p className="text-xs text-neutral-600 mt-0.5">
            Occasional updates about seasonal deals, new destinations, and platform features.
            You'll always receive booking confirmations, receipts and damage reports even when
            this is off — they're required for your active rentals.
          </p>
        </label>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full mt-5" data-testid="profile-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        {t("dashboard.saveProfile")}
      </Button>
    </div>
  );
}
